<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'

import { useRouter } from 'vue-router'

import { useError } from '@/composables/useError'
import { routeNames } from '@/router/routeNames'
import { PathwayRepository } from '@/services/api/pathway'
import type { IPathwayFlowsResponse } from '@/types/PathawayLandingPageTypes'

const { handleError } = useError()
const router = useRouter()

interface StackEntry {
  nodes: IPathwayFlowsResponse[]
  label: string
}

const rootNodes = ref<IPathwayFlowsResponse[]>([])
const currentNodes = ref<IPathwayFlowsResponse[]>([])
const stack = ref<StackEntry[]>([])
const loading = ref(false)
const activeCategorySvgSrc = ref<string | null>(null)

const breadcrumb = computed(() => stack.value.map(s => s.label))
const isRoot = computed(() => stack.value.length === 0)

const VEHICLE_SVG_MAP: { match: string[]; src: string }[] = [
  { match: ['car'], src: '/vehicle-car.svg' },
  { match: ['van'], src: '/vehicle-van.svg' },
  { match: ['bus'], src: '/vehicle-bus.svg' },
  { match: ['custom'], src: '/vehicle-custom.svg' },
]

const FALLBACK_SVG_SRC = '/vehicle-custom.svg'

const getNodeSvgSrc = (node: IPathwayFlowsResponse): string => {
  const needle = node.code.toLowerCase()

  for (const entry of VEHICLE_SVG_MAP) {
    if (entry.match.some(kw => needle.includes(kw))) return entry.src
  }
  return FALLBACK_SVG_SRC
}

const fetchRoot = async (): Promise<void> => {
  loading.value = true
  try {
    const { data } = await PathwayRepository.getAll()
    rootNodes.value = data
    currentNodes.value = data
  } catch (e) {
    handleError(e)
  } finally {
    loading.value = false
  }
}

const selectNode = (node: IPathwayFlowsResponse): void => {
  if (node.children?.length) {
    if (isRoot.value) activeCategorySvgSrc.value = getNodeSvgSrc(node)
    stack.value.push({
      nodes: currentNodes.value,
      label: node.name,
    })
    currentNodes.value = node.children
  } else {
    const flow = node.flows?.[0]
    if (!flow) return
    router.push({ name: routeNames.Pathway, params: { flowUuid: flow.uuid } })
  }
}

const goBack = (): void => {
  const prev = stack.value.pop()
  if (!prev) return
  currentNodes.value = prev.nodes
  if (isRoot.value) activeCategorySvgSrc.value = null
}

const goToRoot = (): void => {
  stack.value = []
  currentNodes.value = rootNodes.value
  activeCategorySvgSrc.value = null
}

onMounted(fetchRoot)
</script>

<template>
  <section
    id="config"
    class="border-pw-line bg-pw-ink text-pw-paper border-b px-5 py-16 md:px-14 md:py-24"
  >
    <div class="mx-auto max-w-360">
      <div
        class="border-pw-line mb-10 flex flex-col gap-3 border-b pb-5 md:mb-5 md:grid md:grid-cols-[auto_1fr_auto] md:items-end md:gap-8"
      >
        <div
          class="text-pw-muted font-mono text-[12px] font-semibold tracking-[0.14em]"
        >
          [ 02 / FLOTA ]
        </div>
        <div>
          <h2
            class="font-narrow m-0 text-[clamp(40px,5vw,72px)] leading-[0.95] font-extrabold tracking-[-0.02em]"
          >
            Wybierz typ pojazdu.
          </h2>
        </div>
        <button
          v-if="!isRoot"
          class="border-pw-line text-pw-muted hover:border-pw-paper hover:text-pw-paper inline-flex cursor-pointer items-center gap-2 border px-4 py-2 font-mono text-[12px] transition-colors"
          @click="goBack"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M19 12H5M11 6l-6 6 6 6" />
          </svg>
          Wróć
        </button>
      </div>

      <nav
        v-if="breadcrumb.length"
        class="text-pw-muted mb-5 flex items-center gap-2 font-mono text-[11px]"
      >
        <button
          class="text-pw-orange cursor-pointer underline-offset-2 hover:underline"
          @click="goToRoot"
        >
          Flota
        </button>
        <template v-for="(crumb, i) in breadcrumb" :key="i">
          <span>/</span>
          <span
            :class="
              i === breadcrumb.length - 1
                ? 'text-pw-paper'
                : 'text-pw-orange cursor-pointer underline-offset-2 hover:underline'
            "
          >
            {{ crumb }}
          </span>
        </template>
      </nav>

      <div
        v-if="loading"
        class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4"
      >
        <div
          v-for="i in 4"
          :key="i"
          class="border-pw-line bg-pw-ink-2 h-70 animate-pulse border"
        ></div>
      </div>

      <Transition name="slide" mode="out-in">
        <div
          v-if="!loading && isRoot"
          :key="'root'"
          class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4"
        >
          <div
            v-for="node in currentNodes"
            :key="node.uuid"
            class="group border-pw-line bg-pw-ink-2 hover:border-pw-yellow hover:bg-pw-ink-3 relative flex cursor-pointer flex-col overflow-hidden border transition-all hover:-translate-y-0.5"
            @click="selectNode(node)"
          >
            <div
              class="type-card-media relative grid aspect-16/11 place-items-center overflow-hidden"
            >
              <div
                class="group-hover:border-pw-yellow border-pw-line bg-pw-ink text-pw-paper group-hover:bg-pw-orange group-hover:text-pw-ink absolute top-3.5 right-3.5 z-2 grid h-7 w-7 place-items-center border transition-colors"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <path d="M7 17L17 7M9 7h8v8" />
                </svg>
              </div>
              <img :src="getNodeSvgSrc(node)" class="h-auto w-4/5" alt="" />
            </div>
            <div class="flex flex-col gap-1.5 p-5">
              <span
                class="text-pw-orange font-mono text-[10px] tracking-[0.14em] uppercase"
                >{{ node.code }}</span
              >
              <h3 class="font-narrow m-0 text-[22px] font-bold tracking-tight">
                {{ node.name }}
              </h3>
              <div
                v-if="node.children?.length"
                class="border-pw-line text-pw-muted mt-1.5 border-t pt-3 font-mono text-[11px]"
              >
                {{ node.children.length }}
                {{
                  node.children.length === 1
                    ? 'opcja'
                    : node.children.length < 5
                      ? 'opcje'
                      : 'opcji'
                }}
              </div>
            </div>
          </div>
        </div>

        <div
          v-else-if="!loading"
          :key="stack.length"
          class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        >
          <div
            v-for="node in currentNodes"
            :key="node.uuid"
            class="group border-pw-line bg-pw-ink-2 hover:border-pw-yellow hover:bg-pw-ink-3 relative flex cursor-pointer flex-col overflow-hidden border transition-all hover:-translate-y-0.5"
            @click="selectNode(node)"
          >
            <div
              class="type-card-media relative grid aspect-video place-items-center overflow-hidden"
            >
              <div
                class="border-pw-line bg-pw-ink text-pw-paper group-hover:border-pw-yellow group-hover:bg-pw-orange group-hover:text-pw-ink absolute top-3 right-3 z-2 grid h-6 w-6 place-items-center border transition-colors"
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <path d="M7 17L17 7M9 7h8v8" />
                </svg>
              </div>
              <img
                :src="activeCategorySvgSrc ?? getNodeSvgSrc(node)"
                class="h-auto w-3/4 opacity-80 transition-opacity group-hover:opacity-100"
                alt=""
              />
            </div>
            <div class="flex flex-col gap-1 p-4">
              <div
                class="text-pw-muted font-mono text-[10px] tracking-widest uppercase"
              >
                {{ node.code }}
              </div>
              <h3 class="font-narrow m-0 text-[20px] font-bold tracking-tight">
                {{ node.name }}
              </h3>
              <div
                v-if="node.children?.length"
                class="border-pw-line text-pw-muted mt-1 border-t pt-2 font-mono text-[10px]"
              >
                {{ node.children.length }}
                {{
                  node.children.length === 1
                    ? 'opcja'
                    : node.children.length < 5
                      ? 'opcje'
                      : 'opcji'
                }}
              </div>
              <div
                v-else-if="node.flows?.length"
                class="border-pw-line text-pw-orange mt-1 border-t pt-2 font-mono text-[10px]"
              >
                Skonfiguruj →
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </section>
</template>

<style scoped>
.type-card-media {
  background: linear-gradient(180deg, #1f2228 0%, #0d0e10 100%);
}

.type-card-media::after {
  content: '';
  position: absolute;
  inset: 0;
  background-image: radial-gradient(
    circle at 1px 1px,
    rgba(255, 255, 255, 0.04) 1px,
    transparent 0
  );
  background-size: 16px 16px;
  pointer-events: none;
}

.slide-enter-active,
.slide-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.slide-enter-from {
  opacity: 0;
  transform: translateX(16px);
}
.slide-leave-to {
  opacity: 0;
  transform: translateX(-16px);
}
</style>
